import { Component, OnInit, ViewChild, ChangeDetectorRef, Input } from '@angular/core';
import { IOpsViewerMessagesService } from '../../../services/services';
// Azure direct SDK imports removed for listing; retained only for upload/delete operations requiring direct blob access.
import { BlockBlobClient } from '@azure/storage-blob';
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { firstValueFrom } from 'rxjs';
import { IUserManagerService } from '../../../services/iUserManager.service';
import { User, FilesClient, FileItem as ApiFileItem } from '../../../services/nswag/nswagclient';
import { UserRoles } from '../../../models/userRole';


@Component({
    selector: 'app-filelist',
    templateUrl: './filelist.component.html',
    styleUrls: ['./filelist.component.css'],
    standalone: false
})
export class FilelistComponent implements OnInit {

  @ViewChild(FileUpload)
  _fileUploader!: FileUpload;
  _consumerJobId = '';
  get consumerJobId(): string {
    return this._consumerJobId;
  }
  @Input() set consumerJobId(val: string | null | undefined) {
    this._consumerJobId = val ?? '';
    if (this.currentUser) {
      this.initialize(this._consumerJobId, this.currentUser);
    }
  };
  // New optional company context. If provided (and no job id) we scope files to company/{companyId}
  _companyId = '';
  get companyId(): string { return this._companyId; }
  @Input() set companyId(val: string | null | undefined) {
    this._companyId = val ?? '';
    if (this.currentUser) {
      this.initialize(this.consumerJobId, this.currentUser);
    }
  }
  @Input() isVendorJob = true;

  constructor(
    private _messageService: IOpsViewerMessagesService,
    private _changeDet: ChangeDetectorRef,
    private _userManagerService: IUserManagerService,
    private _filesClient: FilesClient,
  ) {
    console.warn("constructor");
  }

  uploadedFiles: any[] = [];
  categories: string[] = [];
  files: ApiFileItem[] = [];
  results: string[] = [];
  fileContents = new Map<string, string>();
  rowGroupMetadata: any;
  canUpload = false;
  currentUser: User | null = null;

  get isGlobal(): boolean {
  // Global if neither job nor company context provided
  return !this.consumerJobId && !this.companyId;
  }

  async ngOnInit() {
    const user$ = this._userManagerService.getCurrentUser();
    this.currentUser = await firstValueFrom(user$);
    await this.initialize(this.consumerJobId, this.currentUser);

  }

  async initialize(_jobId: string, user: User) {
    await this.loadFiles();

    if (this.isGlobal || user.isGlobalAdmin) {
      this.canUpload = !!user.isGlobalAdmin;
    } else {
      //use current company role
      const userRole = (user.companyRoles ?? []).find(r => r.companyId === this.companyId);
      this.canUpload = userRole ? userRole.role === UserRoles.admin || userRole.role === UserRoles.editor : false;
    }
  }


  getScriptSrc(txt: string): string {
    const regex = /<script.*src="(.*)".*<\/script>/g;
    const match = regex.exec(txt);
    if (match && match.length > 1) {
      console.warn(match[1]);
      return match[1];
    }
    return '';
  }


  async loadFiles() {
    console.warn("loadFiles via API");
    let directoryPrefix = 'global2';
    if (this.isGlobal) {
      directoryPrefix = 'global2';
    } else if (this.consumerJobId) {
      directoryPrefix = 'job/' + this.consumerJobId;
    } else if (this.companyId) {
      directoryPrefix = 'company/' + this.companyId;
    }
    this._changeDet.detach();
    this.files = [];
    try {
      const listResult = await firstValueFrom(this._filesClient.list(directoryPrefix, 500));
      if (listResult && listResult.items) {
        for (const apiItem of listResult.items) {
          this.loadFile(apiItem);
          if (this.isLink(apiItem) && apiItem.url) {
            try {
              const contents = await this.fetchFileContents(apiItem.url);
              if (apiItem.name) {
                this.fileContents.set(apiItem.name, contents);
              }
              this.loadScriptForContents(contents);
            } catch (err) { console.error('Failed to load content for', apiItem.name, err); }
          }
        }
      }
      this.files = this.files.sort((a, b) => {
        const aCat = this.getMeta(a, 'category');
        const bCat = this.getMeta(b, 'category');
        if (!aCat && !bCat) return 0;
        if (!aCat) return 1;
        if (!bCat) return -1;
        return aCat.localeCompare(bCat);
      });
      this.updateRowGroupMetaData();
    } finally {
      this._changeDet.reattach();
      this._changeDet.detectChanges();
    }
  }
  search(event: { query: string }) {
    this.results = this.categories.filter((s, i, d) => { return s.includes(event.query) });
  }
  loadFile(file: ApiFileItem) {
    if (!file) return;
    this.files.push(file);
    const category = this.getMeta(file, 'category');
    if (category && !this.categories.includes(category)) this.categories.push(category);
  }
  getHref(file: ApiFileItem) { return file.url || ''; }
  isLink(file: ApiFileItem) { return (file.name ?? '').endsWith('.html'); }
  loadScriptForContents(contents: string) {
    let scriptSrc = this.getScriptSrc(contents);
    if (!scriptSrc) return;
    this.loadScript(scriptSrc);

  }
  async getContent(file: ApiFileItem) { return file.url ? this.fetchFileContents(file.url) : ''; }
  private async fetchFileContents(url: string): Promise<string> { const resp = await fetch(url); return await resp.text(); }
  // Build full blob path (relative inside container) based on current context
  private buildBlobPath(fileName: string): string {
    if (this.isGlobal) return `global2/${fileName}`;
    if (this.consumerJobId) return `job/${this.consumerJobId}/${fileName}`;
    if (this.companyId) return `company/${this.companyId}/${fileName}`;
    return fileName; // fallback
  }

  // Get a writable SAS URL for a blob we intend to upload/replace.
  // Current backend only issues read SAS; if write/delete perms get added server-side, update query accordingly.
  private async getWritableBlobClient(blobPath: string): Promise<BlockBlobClient> {
    // Expect server to allow issuing SAS for a target blob even if it doesn't yet exist (standard SAS behavior).
    const sas = await firstValueFrom(this._filesClient.getFileSasToken(blobPath));
    if (!sas || !sas.url) throw new Error(`Failed to obtain SAS for blob ${blobPath}`);
    return new BlockBlobClient(sas.url);
  }
  async upload(event: FileUploadHandlerEvent) {
    for (let i = 0; i < event.files.length; i++) {
      const file = event.files[i];
      const fileWithMetadata = file as File & { description?: string; category?: string };
      const blobPath = this.buildBlobPath(file.name);
      const blockBlobClient = await this.getWritableBlobClient(blobPath);
      this._fileUploader.uploading = true;
      await blockBlobClient.uploadData(file, { blobHTTPHeaders: { blobContentType: file.type } }); try {
        let description: string;
        description = fileWithMetadata.description || "";
        description = description.replace(/([\n])/g, "\\n");
        await blockBlobClient.setMetadata({ category: fileWithMetadata.category || "", description: description });
      } catch (err) {
        this._messageService.errorMessage(err, "could not set description. It may be too long");
        throw err;
      }
      this._fileUploader.clear();
      this._fileUploader.uploading = false;
    }
    await this.loadFiles();
    this._messageService.successMessage("Upload", "File Uploaded");
  }

  isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }
  formatDescription(description: string): string {
    if (!description) return "";
    return description.replace(/(\\n)/g, "<br />");
  }
  formatSize(bytes: number) {
    if (bytes == 0) {
      return '0 B';
    }
    let k = 1024,
      dm = 3,
      sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
  remove(event: Event, file: File) {
    this._fileUploader.clearInputElement();
    this._fileUploader.files = this._fileUploader.files.filter(f => f !== file);
  }
  async confirmDelete(event: Event, file: ApiFileItem) {
    if (!file) return;
    const ok = window.confirm(`Delete file "${file.name}"?`);
    if (!ok) return;
    await this.delete(event, file);
    this._messageService.successMessage("Delete", "File deleted");
  }
  async delete(event: Event, file: ApiFileItem) {
    if (!file || !file.name) return;
    const blobPath = file.name; // server provided full path in Name
    const sas = await firstValueFrom(this._filesClient.getFileSasToken(blobPath));
    if (!sas || !sas.url) {
      this._messageService.errorMessage('Could not get SAS for delete', 'Delete Failed');
      return;
    }
    // NOTE: If current SAS is read-only, delete will fail with 403. Server must grant Delete permission.
    const client = new BlockBlobClient(sas.url);
    await client.deleteIfExists();
    await this.loadFiles();
  }
  getCategory(file: ApiFileItem) {
    return this.getMeta(file, 'category');
  }

  public loadScript(src: string) {
    let isFound = false;
    const scripts = document.getElementsByTagName("script");
    for (let i = 0; i < scripts.length; ++i) {
      const scriptSrc = scripts[i].getAttribute('src');
      if (scriptSrc != null && scriptSrc.includes(src)) {
        isFound = true;
      }
    }

    if (!isFound || true) {
      const dynamicScripts = [src];

      for (let i = 0; i < dynamicScripts.length; i++) {
        let node = document.createElement('script');
        node.src = dynamicScripts[i];
        node.type = 'text/javascript';
        node.async = true;
        //node.charset = 'utf-8';
        document.getElementsByTagName('head')[0].appendChild(node);
      }

    }
  }

  getMeta(file: ApiFileItem, key: string): string {
    const anyFile: any = file as any;
    if (anyFile && anyFile.metadata && typeof anyFile.metadata === 'object') {
      return anyFile.metadata[key] || '';
    }
    return '';
  }

  updateRowGroupMetaData() {
    this.rowGroupMetadata = {};
  if (this.files) {
      for (let i = 0; i < this.files.length; i++) {
        let rowData = this.files[i];
        let brand = this.getCategory(rowData);
        if (i == 0) {
          this.rowGroupMetadata[brand] = { index: 0, size: 1 };
        }
        else {
          let previousRowData = this.files[i - 1];
          let previousRowGroup = this.getCategory(previousRowData);
          if (brand === previousRowGroup)
            this.rowGroupMetadata[brand].size++;
          else
            this.rowGroupMetadata[brand] = { index: i, size: 1 };
        }
      }
    }
  }

  getFileContent(fileName: string | undefined): string {
    if (!fileName) {
      return '';
    }
    return this.fileContents.get(fileName) ?? '';
  }


}

